import { INTENSITY_MAX, intensityColorVar } from "@/lib/intensity";

type IntensityIndicatorSize = "sm" | "md";

const DOT_CLASS: Record<IntensityIndicatorSize, string> = {
  sm: "h-2 w-2",
  md: "h-3 w-3",
};

const BADGE_CLASS: Record<IntensityIndicatorSize, string> = {
  sm: "h-4 min-w-4 px-1 text-xs",
  md: "h-5 min-w-5 px-1.5 text-xs",
};

interface IntensityIndicatorProps {
  level: number;
  /** Dot/badge scale — defaults to "sm" (the most common call site). */
  size?: IntensityIndicatorSize;
  className?: string;
  title?: string;
}

/**
 * Shared Intensity display (system-design §6.2.5 — Intensity is the Pod's
 * points-demand target, 1–{@link INTENSITY_MAX}). Replaces the duplicated
 * inline "IntensityDots"/"IntensityRibbon" components that assumed a max of 8.
 *
 * - Level ≤ 8: the familiar graduated dot row (dots 1..level filled with
 *   `intensityColorVar(i)`, the remainder up to 8 shown muted) — unchanged
 *   look for the common case.
 * - Level ≥ 9: 20 individual dots would be unreadable, so it collapses to a
 *   compact numeric badge colored via `intensityColorVar(level)` (banded to
 *   the darkest `--intensity-8` stop).
 */
export function IntensityIndicator({
  level,
  size = "sm",
  className,
  title,
}: IntensityIndicatorProps) {
  const label = title ?? `Intensity ${level}`;

  if (level <= 8) {
    return (
      <div className={`flex items-center gap-0.5 ${className ?? ""}`} title={label}>
        {Array.from({ length: 8 }, (_, i) => i + 1).map((i) => (
          <span
            key={i}
            className={`${DOT_CLASS[size]} rounded-full`}
            style={{ background: i <= level ? intensityColorVar(i) : "var(--muted)" }}
          />
        ))}
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold text-white tabular-nums ${BADGE_CLASS[size]} ${className ?? ""}`}
      style={{ background: intensityColorVar(level) }}
      title={label}
    >
      {Math.min(level, INTENSITY_MAX)}
    </span>
  );
}
